import log from "loglevel";

// *****************
// DISTANCE
// *****************

const getSphericalDistance = function (latMax, longMax, latMin, longMin) {
  // NEWS North East South West
  // From https://www.movable-type.co.uk/scripts/latlong.html
  const R = 6371e3; // metres
  const φ1 = (latMax * Math.PI) / 180; // φ, λ in radians
  const φ2 = (latMin * Math.PI) / 180;
  const Δφ = ((latMin - latMax) * Math.PI) / 180;
  const Δλ = ((longMax - longMin) * Math.PI) / 180;

  const a =
    Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
    Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  const sectionLength = R * c; // in metres
  return sectionLength;
};

const getPlanarDistanceSquared = function (latLngObject0, latLngObject1) {
  let aSquared =
    (latLngObject0.lat - latLngObject1.lat) *
    (latLngObject0.lat - latLngObject1.lat);
  let bSquared =
    (latLngObject0.lng - latLngObject1.lng) *
    (latLngObject0.lng - latLngObject1.lng);
  return aSquared + bSquared;
};

// *****************
// BOUND BOX
// *****************

class BoundBox {
  constructor(latLngBoundsLiteral) {
    this.north = latLngBoundsLiteral.north;
    this.east = latLngBoundsLiteral.east;
    this.south = latLngBoundsLiteral.south;
    this.west = latLngBoundsLiteral.west;
  }

  get literal() {
    return {
      north: this.north,
      east: this.east,
      south: this.south,
      west: this.west,
    };
  }

  calcSize() {
    const EW = getSphericalDistance(
      // North East to North West
      this.north,
      this.east,
      this.north,
      this.west
    );
    const NS = getSphericalDistance(
      // North West to South West
      this.north,
      this.west,
      this.south,
      this.west
    );
    return { x: EW, y: NS };
  }

  get size() {
    return this.calcSize();
  }

  get increaseBounds() {
    const increaseEW = 500 * 0.000009; //has to be above 120. set minimum as 500 m
    const increaseNS = 500 * 0.000009; //has to be above 120. set minimum as 500 m
    return new BoundBox({
      north: this.north + increaseNS,
      east: this.east + increaseEW,
      south: this.south - increaseNS,
      west: this.west - increaseEW,
    });
  }

  get double() {
    // calculate double
    const increaseEW = (this.size.x * 0.000009) / 2;
    const increaseNS = (this.size.y * 0.000009) / 2;

    return new BoundBox({
      north: this.north + increaseNS,
      east: this.east + increaseEW,
      south: this.south - increaseNS,
      west: this.west - increaseEW,
    });
  }

  isPointWithin(latLng) {
    return (
      latLng.lat < this.north &&
      latLng.lat > this.south &&
      latLng.lng < this.east &&
      latLng.lng > this.west
    );
  }

  isBoxWithin(boundBox) {
    return (
      this.isPointWithin({ lat: boundBox.north, lng: boundBox.west }) &&
      this.isPointWithin({ lat: boundBox.south, lng: boundBox.east })
    );
  }

  // Static function to generate BoundBox

  static floatFromArray(func, arr) {
    return parseFloat(func.apply(null, arr).toFixed(14));
  }

  static createFromMarkers = (allPoints) => {
    if (allPoints.length === 0) {
      return new BoundBox({
        north: 12.990089,
        south: 12.95655,
        east: 77.618526,
        west: 77.5773,
      });
    }
    const lats = allPoints.map((o) => o.lat);
    const longs = allPoints.map((o) => o.lng);

    const latMax = this.floatFromArray(Math.max, lats);
    const latMin = this.floatFromArray(Math.min, lats);
    const longMin = this.floatFromArray(Math.min, longs);
    const longMax = this.floatFromArray(Math.max, longs);

    return new BoundBox({
      south: latMin,
      west: longMin,
      north: latMax,
      east: longMax,
    });
  };
}

// *****************
// DISTANCES
// *****************

const insertPointIntoPath = function (sitePlanCoords, toInsert) {
  let Result = [];
  let distanceSquared = [];
  let minD = 1000000000000;
  let ind = -1;
  // sitePlanCoords.push(toInsert);
  for (let i = 0; i < sitePlanCoords.length; i++) {
    distanceSquared[i] = getPlanarDistanceSquared(sitePlanCoords[i], toInsert);
    if (minD > distanceSquared[i]) {
      minD = distanceSquared[i];
      ind = i;
    }
    Result[0] = i;
  }
  if (ind == 0) {
    if (distanceSquared[distanceSquared.length - 1] > distanceSquared[1]) {
      Result[1] = 1;
      sitePlanCoords.splice(1, 0, toInsert);
    } else {
      Result[1] = distanceSquared.length - 1;
      sitePlanCoords.splice(0, 0, toInsert);
    }
  } else if (ind == distanceSquared.length - 1) {
    if (distanceSquared[ind - 1] > distanceSquared[0]) {
      Result[1] = 0;
      sitePlanCoords.splice(0, 0, toInsert);
    } else {
      Result[1] = ind - 1;
      sitePlanCoords.splice(ind, 0, toInsert);
    }
  } else if (distanceSquared[ind - 1] && distanceSquared[ind + 1]) {
    if (distanceSquared[ind - 1] > distanceSquared[ind + 1]) {
      Result[1] = ind + 1;
      sitePlanCoords.splice(ind + 1, 0, toInsert);
    } else {
      Result[1] = ind;
      sitePlanCoords.splice(ind, 0, toInsert);
    }
  }
  return sitePlanCoords;
};

const checkifPolygonLinesIntersect = (sitePlanCoords) => {
  for (let i = 0; i < sitePlanCoords.length; i++) {
    for (let j = i; j < sitePlanCoords.length; j++) {
      let line1 = [
        sitePlanCoords[i],
        sitePlanCoords[(i + 1) % sitePlanCoords.length],
      ];
      let line2 = [
        sitePlanCoords[j],
        sitePlanCoords[(j + 1) % sitePlanCoords.length],
      ];
      if (
        intersects(
          line1[0].lat,
          line1[0].lng,
          line1[1].lat,
          line1[1].lng,
          line2[0].lat,
          line2[0].lng,
          line2[1].lat,
          line2[1].lng
        )
      ) {
        return true;
      }
    }
  }
  return false;
};

// https://stackoverflow.com/a/24392281
// returns true if the line from (a,b)->(c,d) intersects with (p,q)->(r,s)
function intersects(a, b, c, d, p, q, r, s) {
  var det, gamma, lambda;
  det = (c - a) * (s - q) - (r - p) * (d - b);
  if (det === 0) {
    return false;
  } else {
    lambda = ((s - q) * (r - a) + (p - r) * (s - b)) / det;
    gamma = ((b - d) * (r - a) + (c - a) * (s - b)) / det;
    return 0 < lambda && lambda < 1 && 0 < gamma && gamma < 1;
  }
}

// Taken from (and edited) https://stackoverflow.com/questions/3396088/how-do-i-remove-an-object-from-an-array-with-javascript/3396198#3396198
const removePointFromPath = function (sitePlanCoords, toRemove) {
  const index = sitePlanCoords.findIndex(
    (e) => e.lat === toRemove.lat && e.lng === toRemove.lng
  );
  if (index > -1) {
    sitePlanCoords.splice(index, 1);
  }

  return sitePlanCoords;
};

const normalizeLatLng = (point, boundBox) => {
  const nSize = {
    x: boundBox.east - boundBox.west,
    y: boundBox.north - boundBox.south,
  };
  const x = (point.lng - boundBox.west) / nSize.x;
  const y = (boundBox.north - point.lat) / nSize.y;
  return { x, y };
};

const radians = (deg) => {
  return (deg * Math.PI) / 180;
};

const getSolarData = (latitude, date, time) => {
  if (time - 12 == 0) {
    time = 12.01;
  }
  const timeRad = radians(15 * (time - 12));
  const latRad = radians(latitude);
  const hemisphere = latitude / Math.abs(latitude);
  let season;
  if (hemisphere > 0) {
    season = date / 182.5;
  } else {
    season = 1 - date / 182.5;
  }

  const declination = -Math.asin(
    0.39779 *
      Math.cos(
        radians(
          0.98565 * (date + 10) +
            1.914 * Math.sin(radians(0.98565) * (date - 2))
        )
      )
  );

  // const declination =
  //   radians(-23.44) * Math.cos(radians((360 / 365) * (date + 10)));

  const sunZenith = Math.acos(
    Math.sin(declination) * Math.sin(latRad) +
      Math.cos(declination) * Math.cos(latRad) * Math.cos(timeRad)
  );
  let sunAzimuth = Math.acos(
    (Math.sin(declination) * Math.cos(latRad) -
      Math.cos(timeRad) * Math.cos(declination) * Math.sin(latRad)) /
      Math.sin(sunZenith)
  );

  // let sunAzimuth = Math.acos(
  //   (Math.sin(declination) - Math.cos(sunZenith) * Math.sin(latRad)) /
  //     (Math.sin(sunZenith) * Math.cos(latRad))
  // );

  if (time - 12 < 0) {
    sunAzimuth = -sunAzimuth;
  }

  // log.info(time, sunAzimuth);
  const x = Math.cos(sunAzimuth - Math.PI / 2) * Math.sin(sunZenith);
  const y = Math.sin(sunAzimuth - Math.PI / 2) * Math.sin(sunZenith);
  const z = Math.cos(sunZenith);

  return {
    time: time,
    date: date,
    season: season,
    declination: declination,
    solarZenith: sunZenith,
    solarAzimuth: sunAzimuth,
    position: { x: x, y: y, z: z },
  };
};

// *****************
// High Res Markers
// *****************

const highResMarkers = (markersTemp) => {
  let markers = [];
  markersTemp.push(markersTemp[0]);
  markers.push(markersTemp[0]);
  for (let i = 1; i < markersTemp.length; i++) {
    let n =
      getPlanarDistanceSquared(markersTemp[i - 1], markersTemp[i]) *
      3600000 *
      4;
    for (let j = 0; j < n; j++) {
      let lat = lerp(markersTemp[i - 1].lat, markersTemp[i].lat, j / n);
      let lng = lerp(markersTemp[i - 1].lng, markersTemp[i].lng, j / n);
      markers.push({ lat: lat, lng: lng });
    }
    markers.push(markersTemp[i]);
  }
  return markers;
};

function lerp(start, end, amt) {
  return (1 - amt) * start + amt * end;
}

const heightAtLatLng = (latLng, terrain) => {
  let vert = normalizeLatLng(latLng, terrain.boundBox);
  // log.info(latLng, terrain.boundBox, vert.x, vert.y);
  let pos = {};
  pos.x = vert.x * (terrain.noOfVertices.x + 1) - 1;
  pos.y = vert.y * (terrain.noOfVertices.y + 1) - 1;
  const h00 = terrain.altitudes[Math.floor(pos.x)][Math.floor(pos.y)];
  const h01 = terrain.altitudes[Math.floor(pos.x)][Math.floor(pos.y + 1)];
  const h10 = terrain.altitudes[Math.floor(pos.x + 1)][Math.floor(pos.y)];
  const h11 = terrain.altitudes[Math.floor(pos.x + 1)][Math.floor(pos.y + 1)];
  const indexX = Math.abs(pos.x) - Math.floor(pos.x);
  const indexY = Math.abs(pos.y) - Math.floor(pos.y);
  vert.z =
    h00 * (1 - indexX) * (1 - indexY) +
    h10 * indexX * (1 - indexY) +
    h01 * (1 - indexX) * indexY +
    h11 * (indexX * indexY);
  return vert;
};
export {
  highResMarkers,
  heightAtLatLng,
  normalizeLatLng,
  getSphericalDistance,
  getPlanarDistanceSquared,
  BoundBox,
  insertPointIntoPath,
  removePointFromPath,
  getSolarData,
  checkifPolygonLinesIntersect,
};
